import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, FileText, Plus } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { HistorySection } from '../components/EntityHistory'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge, type Variant } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { Drawer } from '../components/ui/Drawer'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select, Textarea } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { formatDate } from '../lib/utils'

type Invoice = {
  id: string
  clientId: string
  clientName?: string | null
  number: string
  year: number
  issueDate: string
  dueDate: string | null
  paidDate: string | null
  amountNet: string
  amountVat: string
  amountGross: string
  withholdingTax: string
  stampDuty: string
  currency: string
  vatRate: string | null
  vatRegime: string
  reverseCharge: boolean
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'
  paymentMethod: string | null
  paymentReference: string | null
  sdiCode: string | null
  sdiSentAt: string | null
  sdiAcceptedAt: string | null
  notes: string | null
  version: number
  updatedAt: string
}

type ListResult = { results: Invoice[]; total: number }
type Client = { id: string; name: string }
type InvoiceDetailResult = { found: boolean; invoice?: Invoice; lines?: InvoiceLine[] }
type InvoiceLine = {
  id: string
  lineNumber: number
  description: string
  quantity: string
  unitPrice: string | null
  vatRate: string
  lineNet: string
  lineVat: string
  lineGross: string
}

const STATUS_VARIANT: Record<Invoice['status'], Variant> = {
  draft: 'muted',
  sent: 'info',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'muted',
}

function fmtEUR(s: string | number | null | undefined, currency = 'EUR') {
  if (s == null || s === '') return '—'
  const n = typeof s === 'string' ? Number(s) : s
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(n)
}

export function InvoicesPage() {
  const qc = useQueryClient()
  const [year, setYear] = useState('')
  const [status, setStatus] = useState('')
  const [clientId, setClientId] = useState('')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', { year, status, clientId, overdueOnly }],
    queryFn: () => {
      const p = new URLSearchParams()
      if (year) p.set('year', year)
      if (status) p.set('status', status)
      if (clientId) p.set('clientId', clientId)
      if (overdueOnly) p.set('overdueOnly', 'true')
      p.set('limit', '200')
      return apiClient.get<ListResult>(`/invoices?${p}`)
    },
  })

  const clientsQuery = useQuery({
    queryKey: ['clients', { all: true }],
    queryFn: () => apiClient.get<{ results: Client[] }>('/clients?limit=200'),
  })

  const totals = useMemo(() => {
    const items = data?.results ?? []
    const sum = items.reduce(
      (acc, i) => {
        const gross = Number(i.amountGross)
        acc.gross += gross
        if (i.status === 'paid') acc.paid += gross
        if (i.status === 'sent' || i.status === 'overdue') acc.outstanding += gross
        return acc
      },
      { gross: 0, paid: 0, outstanding: 0 },
    )
    return sum
  }, [data])

  return (
    <AppLayout>
      <PageHeader
        title="Fatture"
        description={
          data
            ? `${data.total} fatture · ${fmtEUR(totals.gross)} totale · ${fmtEUR(totals.paid)} incassato · ${fmtEUR(totals.outstanding)} da incassare`
            : 'Fatturazione Italian fiscal compliant'
        }
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Nuova fattura
          </Button>
        }
      />

      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-2">
              <Label htmlFor="year">Anno</Label>
              <Input
                id="year"
                type="number"
                placeholder="2026"
                value={year}
                onChange={(e) => setYear(e.currentTarget.value)}
              />
            </div>
            <div className="md:col-span-3">
              <Label htmlFor="status">Stato</Label>
              <Select id="status" value={status} onChange={(e) => setStatus(e.currentTarget.value)}>
                <option value="">Tutti</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </div>
            <div className="md:col-span-4">
              <Label htmlFor="clientId">Cliente</Label>
              <Select
                id="clientId"
                value={clientId}
                onChange={(e) => setClientId(e.currentTarget.value)}
              >
                <option value="">Tutti</option>
                {clientsQuery.data?.results.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label htmlFor="overdue">Solo scadute</Label>
              <Select
                id="overdue"
                value={overdueOnly ? 'true' : 'false'}
                onChange={(e) => setOverdueOnly(e.currentTarget.value === 'true')}
              >
                <option value="false">No</option>
                <option value="true">Sì</option>
              </Select>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium w-28">Numero</th>
              <th className="text-left px-5 py-3 font-medium">Cliente</th>
              <th className="text-left px-5 py-3 font-medium">Data</th>
              <th className="text-right px-5 py-3 font-medium">Imponibile</th>
              <th className="text-right px-5 py-3 font-medium">Totale</th>
              <th className="text-left px-5 py-3 font-medium">Scadenza</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
                <tr key={i} className="border-t border-zinc-800">
                  <td className="px-5 py-3" colSpan={7}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : data && data.results.length > 0 ? (
              data.results.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => setOpenInvoiceId(inv.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setOpenInvoiceId(inv.id)
                  }}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40 cursor-pointer transition"
                >
                  <td className="px-5 py-3 font-mono text-zinc-300">
                    {inv.number}/{inv.year}
                  </td>
                  <td className="px-5 py-3 text-zinc-100">{inv.clientName ?? '—'}</td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{formatDate(inv.issueDate)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-zinc-300">
                    {fmtEUR(inv.amountNet, inv.currency)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium text-zinc-100">
                    {fmtEUR(inv.amountGross, inv.currency)}
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-500">
                    {inv.dueDate ? formatDate(inv.dueDate) : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={STATUS_VARIANT[inv.status]}>{inv.status}</Badge>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>
                  <Empty
                    icon={FileText}
                    title="Nessuna fattura"
                    description="Modifica i filtri o crea la prima fattura."
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {openInvoiceId && (
        <InvoiceDetailDrawer
          id={openInvoiceId}
          onClose={() => setOpenInvoiceId(null)}
          onPaid={() => {
            qc.invalidateQueries({ queryKey: ['invoices'] })
          }}
        />
      )}

      <NewInvoiceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clients={clientsQuery.data?.results ?? []}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['invoices'] })
          setCreateOpen(false)
        }}
      />
    </AppLayout>
  )
}

// ============================================================
// Drawer dettaglio fattura
// ============================================================

function InvoiceDetailDrawer({
  id,
  onClose,
  onPaid,
}: {
  id: string
  onClose: () => void
  onPaid: () => void
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => apiClient.get<InvoiceDetailResult>(`/invoices/${id}`),
  })

  const markPaid = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.post(`/invoices/${id}/mark-paid`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] })
      onPaid()
    },
  })

  if (!data?.found) {
    return (
      <Drawer open onClose={onClose} title="Fattura" width="md">
        <div className="p-5">
          {isLoading ? (
            <Skeleton className="h-6 w-2/3" />
          ) : (
            <p className="text-sm text-zinc-400">Fattura non trovata.</p>
          )}
        </div>
      </Drawer>
    )
  }

  const inv = data.invoice as Invoice
  const lines = data.lines ?? []
  const canMarkPaid = inv.status === 'sent' || inv.status === 'overdue'

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${inv.number}/${inv.year}`}
      subtitle={
        <div className="text-xs text-zinc-400">
          {inv.clientName ?? '—'} · emessa {formatDate(inv.issueDate)}
        </div>
      }
      badges={
        <>
          <Badge variant={STATUS_VARIANT[inv.status]}>{inv.status}</Badge>
          <Badge variant="muted">{inv.vatRegime.replace('_', ' ')}</Badge>
          {inv.reverseCharge && <Badge variant="info">reverse charge</Badge>}
        </>
      }
      width="lg"
    >
      <div className="p-5 space-y-5">
        <section className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <KV k="Imponibile" v={fmtEUR(inv.amountNet, inv.currency)} />
          <KV k="IVA" v={fmtEUR(inv.amountVat, inv.currency)} />
          <KV k="Totale" v={fmtEUR(inv.amountGross, inv.currency)} strong />
          {Number(inv.withholdingTax) > 0 && (
            <KV k="Ritenuta d'acconto" v={fmtEUR(inv.withholdingTax, inv.currency)} />
          )}
          {Number(inv.stampDuty) > 0 && <KV k="Bollo" v={fmtEUR(inv.stampDuty, inv.currency)} />}
          {inv.vatRate && <KV k="Aliquota IVA" v={`${inv.vatRate}%`} />}
        </section>

        <section className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <KV k="Data emissione" v={formatDate(inv.issueDate)} />
          {inv.dueDate && <KV k="Scadenza" v={formatDate(inv.dueDate)} />}
          {inv.paidDate && <KV k="Pagata il" v={formatDate(inv.paidDate)} />}
          {inv.paymentMethod && <KV k="Metodo pagamento" v={inv.paymentMethod} />}
          {inv.paymentReference && <KV k="Riferimento" v={inv.paymentReference} />}
        </section>

        {lines.length > 0 && (
          <section>
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Righe</h4>
            <table className="w-full text-xs">
              <thead className="text-zinc-500">
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-2 font-medium">#</th>
                  <th className="text-left py-2 font-medium">Descrizione</th>
                  <th className="text-right py-2 font-medium">Q.tà</th>
                  <th className="text-right py-2 font-medium">Prezzo</th>
                  <th className="text-right py-2 font-medium">IVA%</th>
                  <th className="text-right py-2 font-medium">Totale</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} className="border-b border-zinc-800/50">
                    <td className="py-2 text-zinc-500">{l.lineNumber}</td>
                    <td className="py-2 text-zinc-200">{l.description}</td>
                    <td className="py-2 text-right tabular-nums">{l.quantity}</td>
                    <td className="py-2 text-right tabular-nums">
                      {l.unitPrice ? fmtEUR(l.unitPrice, inv.currency) : '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums">{l.vatRate}%</td>
                    <td className="py-2 text-right tabular-nums font-medium">
                      {fmtEUR(l.lineGross, inv.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {inv.sdiCode && (
          <section className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <KV k="Codice SDI" v={inv.sdiCode} />
            {inv.sdiSentAt && <KV k="SDI inviata" v={formatDate(inv.sdiSentAt)} />}
            {inv.sdiAcceptedAt && <KV k="SDI accettata" v={formatDate(inv.sdiAcceptedAt)} />}
          </section>
        )}

        {inv.notes && (
          <section>
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Note</h4>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{inv.notes}</p>
          </section>
        )}

        {canMarkPaid && (
          <section className="pt-2 border-t border-zinc-800">
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
              Registra pagamento
            </h4>
            <MarkPaidForm onSubmit={(body) => markPaid.mutate(body)} busy={markPaid.isPending} />
            {markPaid.isError && (
              <div className="text-xs text-red-400 mt-2">
                {(markPaid.error as Error)?.message ?? 'Errore'}
              </div>
            )}
          </section>
        )}

        {/* History */}
        <HistorySection entityType="invoice" entityId={inv.id} />
      </div>
    </Drawer>
  )
}

function MarkPaidForm({
  onSubmit,
  busy,
}: {
  onSubmit: (body: Record<string, unknown>) => void
  busy: boolean
}) {
  function onFormSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const body: Record<string, unknown> = {}
    for (const [k, v] of fd.entries()) {
      const s = String(v).trim()
      if (s) body[k] = s
    }
    onSubmit(body)
  }
  return (
    <form onSubmit={onFormSubmit} className="grid grid-cols-3 gap-2">
      <div>
        <Label htmlFor="paidDate">Data</Label>
        <Input id="paidDate" name="paidDate" type="date" />
      </div>
      <div>
        <Label htmlFor="paymentMethod">Metodo</Label>
        <Select id="paymentMethod" name="paymentMethod" defaultValue="bonifico">
          <option value="bonifico">Bonifico</option>
          <option value="rid">RID</option>
          <option value="contanti">Contanti</option>
          <option value="carta">Carta</option>
          <option value="paypal">PayPal</option>
          <option value="compensazione">Compensazione</option>
          <option value="altro">Altro</option>
        </Select>
      </div>
      <div>
        <Label htmlFor="paymentReference">Riferimento</Label>
        <Input id="paymentReference" name="paymentReference" placeholder="CRO" />
      </div>
      <div className="col-span-3 flex justify-end">
        <Button type="submit" disabled={busy}>
          <CheckCircle2 className="size-4" /> {busy ? 'Registrazione…' : 'Marca come pagata'}
        </Button>
      </div>
    </form>
  )
}

// ============================================================
// Nuova fattura (dialog)
// ============================================================

function NewInvoiceDialog({
  open,
  onClose,
  clients,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  clients: Client[]
  onCreated: () => void
}) {
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post('/invoices', body),
    onSuccess: onCreated,
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const body: Record<string, unknown> = {}
    for (const [k, v] of fd.entries()) {
      const s = String(v).trim()
      if (s === '') continue
      if (['year'].includes(k)) body[k] = Number(s)
      else if (['reverseCharge'].includes(k)) body[k] = s === 'true'
      else body[k] = s
    }
    create.mutate(body)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nuova fattura"
      description="Compila i dati base. Le righe si possono aggiungere dopo da MCP o riaprendo la fattura."
      size="lg"
    >
      <form onSubmit={onSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-3">
            <Label htmlFor="number">Numero *</Label>
            <Input id="number" name="number" required maxLength={32} placeholder="2026/001" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="year">Anno *</Label>
            <Input
              id="year"
              name="year"
              type="number"
              required
              defaultValue={new Date().getFullYear()}
            />
          </div>
          <div className="col-span-7">
            <Label htmlFor="clientId">Cliente *</Label>
            <Select id="clientId" name="clientId" required defaultValue="">
              <option value="" disabled>
                Seleziona cliente
              </option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="col-span-4">
            <Label htmlFor="issueDate">Data emissione *</Label>
            <Input
              id="issueDate"
              name="issueDate"
              type="date"
              required
              defaultValue={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="col-span-4">
            <Label htmlFor="dueDate">Scadenza</Label>
            <Input id="dueDate" name="dueDate" type="date" />
          </div>
          <div className="col-span-4">
            <Label htmlFor="vatRegime">Regime IVA</Label>
            <Select id="vatRegime" name="vatRegime" defaultValue="ordinario">
              <option value="ordinario">Ordinario</option>
              <option value="forfettario">Forfettario</option>
              <option value="agevolato">Agevolato</option>
              <option value="reverse_charge">Reverse charge</option>
              <option value="esente">Esente</option>
              <option value="non_imponibile">Non imponibile</option>
            </Select>
          </div>
          <div className="col-span-3">
            <Label htmlFor="amountNet">Imponibile *</Label>
            <Input id="amountNet" name="amountNet" type="number" step="0.01" required />
          </div>
          <div className="col-span-3">
            <Label htmlFor="amountVat">IVA</Label>
            <Input id="amountVat" name="amountVat" type="number" step="0.01" defaultValue="0" />
          </div>
          <div className="col-span-3">
            <Label htmlFor="amountGross">Totale *</Label>
            <Input id="amountGross" name="amountGross" type="number" step="0.01" required />
          </div>
          <div className="col-span-3">
            <Label htmlFor="vatRate">Aliquota IVA %</Label>
            <Input id="vatRate" name="vatRate" type="number" step="0.01" placeholder="22" />
          </div>
          <div className="col-span-3">
            <Label htmlFor="withholdingTax">Ritenuta</Label>
            <Input
              id="withholdingTax"
              name="withholdingTax"
              type="number"
              step="0.01"
              defaultValue="0"
            />
          </div>
          <div className="col-span-3">
            <Label htmlFor="stampDuty">Bollo</Label>
            <Input id="stampDuty" name="stampDuty" type="number" step="0.01" defaultValue="0" />
          </div>
          <div className="col-span-6">
            <Label htmlFor="currency">Valuta</Label>
            <Input id="currency" name="currency" defaultValue="EUR" maxLength={3} />
          </div>
          <div className="col-span-6">
            <Label htmlFor="status">Stato</Label>
            <Select id="status" name="status" defaultValue="draft">
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
            </Select>
          </div>
          <div className="col-span-12">
            <Label htmlFor="notes">Note</Label>
            <Textarea id="notes" name="notes" />
          </div>
        </div>

        {create.isError && (
          <div className="text-sm text-red-400">{(create.error as Error)?.message ?? 'Errore'}</div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800/60">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creazione…' : 'Crea fattura'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function KV({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{k}</dt>
      <dd className={strong ? 'text-zinc-50 font-semibold' : 'text-zinc-200'}>{v}</dd>
    </div>
  )
}
